// src/contexts/SubrolesContext.jsx
// Contexto compartido para subroles de Paz y Salvo.
// Hace UNA sola llamada API independientemente de cuántos componentes consuman el hook.
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { pazYSalvoAPI } from '../services/api';

const SubrolesContext = createContext(null);

export const SubrolesProvider = ({ children }) => {
    const { user, token } = useAuth();
    const userId = user?.id ?? null;
    const hasToken = !!token;

    const [subroles, setSubroles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!userId || !hasToken) {
            setSubroles([]);
            return;
        }

        let cancelled = false;

        const fetch = async () => {
            setLoading(true);
            setError(null);
            try {
                const { data } = await pazYSalvoAPI.getUserSubroles();
                if (!cancelled) setSubroles(data || []);
            } catch (err) {
                if (cancelled || err.name === 'CanceledError' || err.name === 'AbortError') return;
                console.error('SubrolesContext: error al obtener subroles:', err);
                if (!cancelled) { setSubroles([]); setError(err.response?.data?.message || err.message); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetch();
        return () => { cancelled = true; };
    }, [userId, hasToken]);

    const hasSubrole = useCallback((name) =>
        subroles.some(s => s.rol === name && s.isActive), [subroles]);

    const getActiveSubroles = useCallback(() =>
        subroles.filter(s => s.isActive), [subroles]);

    const getDirectSubroles = useCallback(() =>
        subroles.filter(s => s.isActive && s.tipo === 'Directo'), [subroles]);

    const getDelegations = useCallback(() =>
        subroles.filter(s => s.isActive && s.tipo === 'Delegado'), [subroles]);

    const value = React.useMemo(() => ({
        subroles, loading, error,
        hasSubrole, getActiveSubroles, getDirectSubroles, getDelegations,
    }), [subroles, loading, error, hasSubrole, getActiveSubroles, getDirectSubroles, getDelegations]);

    return (
        <SubrolesContext.Provider value={value}>
            {children}
        </SubrolesContext.Provider>
    );
};

export const useSubroles = () => {
    const ctx = useContext(SubrolesContext);
    if (!ctx) throw new Error('useSubroles debe usarse dentro de SubrolesProvider');
    return ctx;
};
